
import { prop, Vue, Options } from "vue-class-component";
import Note from "./components/note.vue";
import SummarySTT from "./components/summary-stt.vue";
import {
  SummarySTTData,
  TicketDetail,
  TicketAction
} from "@/domain/entities/PusatResolusiV2";
import DelDexCodrej from "./components/del-dex-codrej.vue";
import PartnerSection from "./components/partner-section.vue";
import {
  CLIENT_ERROR,
  INTERNET_ERROR,
  INTERNAL_SERVER_ERROR
} from "@/app/infrastructures/misc/Utils";
import Skeleton from "@/app/ui/components/skeleton/index.vue";
import DetailItems from "./components/detail-items.vue";
import ModalFinishIssue from "./components/modal-finish-issue.vue";
import ModalSuccessFinish from "./components/modal-success-finish.vue";
import ModalReopenReason from "./components/modal-reopen-reason.vue";
import { PusatResolusiV2Controller } from "@/app/ui/controllers/PusatResolusiV2Controller";
import {
  FinishIssuePusatResolusiV2Request,
  ReopenPusatResolusiV2Request
} from "@/data/payload/api/PusatResolusiV2ApiRequest";
import { MainAppController } from "@/app/ui/controllers/MainAppController";
import { parsingErrorResponse } from "@/app/infrastructures/misc/Utils";
import IssueSection from "./components/issue-section.vue";
import { PusatResolusiV2Mapper } from "@/data/persistences/mappers/PusatResolusiV2Mapper";
import { getHubData } from "@/app/infrastructures/misc/Cookies";
import { AccountController } from "@/app/ui/controllers/AccountController";

class Props {
  data = prop<TicketDetail>({
    required: true
  });
  action = prop<TicketAction>({
    required: true
  });
  errorState = prop<string>({
    required: true
  });
  isLoading = prop<boolean>({
    required: true
  });
  websocket = prop<WebSocket>({
    required: true
  });
}

@Options({
  components: {
    Note,
    SummarySTT,
    DelDexCodrej,
    PartnerSection,
    Skeleton,
    DetailItems,
    ModalFinishIssue,
    ModalSuccessFinish,
    IssueSection,
    ModalReopenReason
  },
  watch: {
    websocket: {
      handler(ws: WebSocket) {
        if (!ws) return;
        ws.addEventListener("message", async (event) => {
          const mapper = new PusatResolusiV2Mapper();
          const conversation = mapper.convertHistoryChatResponseFromWebsocket(
            event.data
          )[0];

          if (!conversation.type) {
            this.ticketAction = new TicketAction({
              isOpenMultipleTab: conversation.isOpenMultipleTab,
              isAssignedToOther: conversation.isAssignedToOther,
              assignedUsername: conversation.assignedUsername,
              action: conversation.action
            });
          }
        });
      }
    }
  }
})
export default class SummarySection extends Vue.with(Props) {
  CLIENT_ERROR = CLIENT_ERROR;
  INTERNAL_SERVER_ERROR = INTERNAL_SERVER_ERROR;
  INTERNET_ERROR = INTERNET_ERROR;
  activeTab = "summary";
  note = "";
  ticketAction = new TicketAction();

  get id() {
    return this.$route.params.id;
  }

  get issueNumber() {
    return this.data.ticket.caseNumber;
  }
  get partnerData() {
    return {
      reporter: {
        partnerCode: this.data.ticket.createdByPartnerCode,
        partnerName: this.data.ticket.createdByPartnerName,
        partnerUsername: this.data.ticket.createdByUsername,
        partnerTier: this.data.ticket.createdByTier.toLowerCase(),
        partnerHubName: this.data.ticket.createdByHubName,
        isYou: this.data.ticket.isYou,
        date: this.data.ticket.createdAt
      },
      escalation: this.data.ticket.escalatePartner.map((partner) => {
        return {
          partnerCode: partner.escalatePartnerCode,
          partnerName: partner.escalatePartnerName,
          partnerUsername: partner.escalatePartnerUsername,
          date: partner.escalateAt,
          partnerHubName: partner.escalateHubName,
          isYou: partner.isYou,
          finishAt: partner.escalateFinish
        };
      }),
      escalationBefore: this.data.ticket.escalatePartnerHistory.map(
        (partner) => {
          return {
            partnerCode: partner.escalatePartnerCode,
            partnerName: partner.escalatePartnerName,
            partnerUsername: partner.escalatePartnerUsername,
            date: partner.createdAt,
            partnerHubName: partner.escalateHubName,
            isYou: partner.isYou
          };
        }
      ),
      agent: {
        partnerCode: "",
        partnerName: this.data.ticket.agentCsId,
        partnerUsername: this.data.ticket.agentCsName,
        partnerFullname: this.data.ticket.agentCsName
          ? `Lion Parcel - ${this.data.ticket.agentCsName}`
          : "",
        date: this.data.ticket.agentCsAt
      },
      followers: this.data.ticket.followers.map((follower) => {
        return {
          partnerCode: follower.accountPartnerCode,
          partnerName: follower.accountPartnerName,
          partnerUsername: follower.accountUsername,
          partnerHubName: follower.hubName,
          isYou: follower.isYou,
          date: new Date()
        };
      })
    };
  }

  get summaryDetailData() {
    return new SummarySTTData({
      bookingDate: this.data.summary.bookedAt,
      updatedAt: this.data.summary.lastStatusAt,
      sttNo: this.data.ticket.refNo,
      rejectReason: this.data.summary.lastDelivReasonName,
      productType: this.data.summary.product,
      posName: this.data.summary.bookedName,
      posLevel: this.data.summary.bookedTier.toLowerCase(),
      origin: this.data.summary.originCity,
      destination: this.data.summary.destinationCity,
      lastStatus: this.data.summary.lastStatus,
      isOnSLA: this.data.summary.isSla,
      isDFOD: this.data.summary.isDfod,
      isCOD: this.data.summary.isCod,
      estimationDate: this.data.summary.estimatedSlaEndDate,
      dexAttemps: this.data.summary.totalDexAttempt,
      delAttemps: this.data.summary.totalDelAttempt,
      codRejAttemps: this.data.summary.totalCodrejattempt,
      courierName: this.data.summary.lastDelivCourierName,
      lastDelivPartnerName: this.data.summary.lastDelivPartnerName,
      lastDelivAt: this.data.summary.lastDelivAt
    });
  }

  get isDelDexCodrej() {
    const validLabels = [
      this.$t('pusatResolusi.askRevertTicket'),
      this.$t('pusatResolusi.failedToDeliver'),
      this.$t('pusatResolusi.refute')
    ];
    const validStatuses = ["DEL", "DEX", "CODREJ"];

    return (
      validLabels
        .map((label) => label.toLowerCase())
        .includes(this.data.ticket.category.label.toLowerCase()) &&
      validStatuses
        .map((status) => status.toLowerCase())
        .includes(this.data.summary.lastStatus.toLowerCase())
    );
  }

  get isShowNote() {
    return this.action.action.includes("note");
  }

  get reopenTicket() {
    return (
      this.data.ticket.createdBy === AccountController.accountData.account_id &&
      this.data.ticket.status === "RESOLVED"
    );
  }

  get isShowSolveTicketButton() {
    return this.action.action.includes("solve_ticket");
  }

  resetValueChatReopen() {
    PusatResolusiV2Controller.setChatMessageReopen("");
    PusatResolusiV2Controller.setMentionSelectedReopen([]);
    PusatResolusiV2Controller.setUploadedFilesReopen([]);
  }

  isModalReopenReason = false;
  onModalReopenReason() {
    this.isModalReopenReason = !this.isModalReopenReason;
  }

  toggleSnackbar(bool: boolean) {
    this.isReopenSuccess = bool;
  }

  reopenSuccessMessage = "";
  isReopenSuccess = false;
  async onSubmitReopen() {
    // submit reopen
    this.isModalReopenReason = false;
    this.isReopenSuccess = false;
    MainAppController.closeErrorMessage();
    MainAppController.showLoading();

    try {
      const reopen = await PusatResolusiV2Controller.reopenTicket({
        id: Number(this.id),
        payload: new ReopenPusatResolusiV2Request({
          message: PusatResolusiV2Controller.chatMessageReopen,
          mentions: PusatResolusiV2Controller.mentionSelectedFinish,
          attachments: PusatResolusiV2Controller.uploadedFilesFinish.filter(
            (e): e is File => e !== null
          ),
          hubId: getHubData()?.hubId || "0"
        })
      });
      if (reopen) {
        this.showSnackbar();
        this.resetValueChatReopen();
      }
    } catch (error) {
      this.resetValueChatReopen();
      MainAppController.showErrorMessage(
        parsingErrorResponse(
          error,
          "Gagal membuka tiket!",
          this.onSubmitReopen,
          this.resetValueChatReopen
        )
      );
    } finally {
      MainAppController.closeLoading();
    }
  }

  showSnackbar() {
    this.isReopenSuccess = true;
    this.reopenSuccessMessage = `<b>${this.$t('Sukses')}</b><br/>${this.$t('Nomor kendala')} "<b>${this.data.ticket.caseNumber}</b>" ${this.$t('berhasil Eskalasi ulang.')}`;

    setTimeout(() => {
      this.isReopenSuccess = false;
      this.$emit("getDetail");
    }, 3000);
  }

  onSubmitNote() {
    // implement submit note integration here
  }
  // modal finish
  modalFinish = false;
  modalSuccessFinish = false;
  onShowModalFinish() {
    this.modalFinish = !this.modalFinish;
  }
  onShowModalSuccessFinish() {
    this.modalSuccessFinish = !this.modalSuccessFinish;
  }

  resetValueChat() {
    PusatResolusiV2Controller.setChatMessageFinish("");
    PusatResolusiV2Controller.setMentionSelectedFinish([]);
    PusatResolusiV2Controller.setUploadedFilesFinish([]);
  }

  async onSubmitFinish() {
    this.modalFinish = false;
    MainAppController.closeErrorMessage();
    MainAppController.showLoading();

    try {
      const finish = await PusatResolusiV2Controller.finishTicket({
        id: Number(this.id),
        // fill payload when work on ticket chat component
        payload: new FinishIssuePusatResolusiV2Request({
          message: PusatResolusiV2Controller.chatMessageFinish,
          hubId: getHubData()?.hubId || "0",
          mentions: PusatResolusiV2Controller.mentionSelectedFinish,
          attachments: PusatResolusiV2Controller.uploadedFilesFinish.filter(
            (e): e is File => e !== null
          )
        })
      });
      if (finish) {
        this.onShowModalSuccessFinish();
        this.resetValueChat();
      }
    } catch (error) {
      MainAppController.showErrorMessage(
        parsingErrorResponse(
          error,
          this.$t("Gagal menyelesaikan kendala!"),
          this.onSubmitFinish,
          this.resetValueChat
        )
      );
    } finally {
      MainAppController.closeLoading();
    }
  }
}
